import { useCallback, useEffect, useState } from "react";
import {
  useMeeting,
  useMediaDevice,
  useParticipant,
  usePubSub,
  createCameraVideoTrack,
} from "@videosdk.live/react-sdk";
import {
  MicrophoneIcon,
  SpeakerWaveIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import MicLevelMeter from "./MicLevelMeter";
import TestSpeakersButton from "./TestSpeakersButton";
import usePubsubFunction from "../utils/usePubsubFunction";
import { useAppContext } from "../AppContextDef";

/**
 * DeviceControls — the mic / speaker / camera picker row shown under the video.
 *
 * Self-contained: it reads and switches devices through VideoSDK itself, so a
 * parent only has to drop <DeviceControls /> below its <ParticipantView />.
 *
 *   - Mic    : getMics() + stash id in context,  live meter in the popover
 *   - Speaker: getPlaybackDevices() + setSinkId, "Test Speakers" in the popover
 *   - Camera : getWebcams() + locally-tracked id (the SDK's selectedCameraDevice
 *              is stale/unreliable); switching recreates the track via
 *              disableWebcam → createCameraVideoTrack → enableWebcam
 *
 * The dropdowns themselves are the shadcn/ui <Select> (a Radix wrapper); this
 * component only wires them to VideoSDK. The live mic meter and the "Test
 * Speakers" button are passed as the <SelectContent> `footer` — pinned below
 * the device list inside the popover (per Figma). The wrapper isolates the
 * footer's pointer/keyboard events so Radix's listbox doesn't swallow them.
 *
 * Device lists refresh on mount and whenever a device is plugged in / removed.
 * Dropdowns open upward (they sit at the bottom of the video), matching design.
 *
 * @param {string} [className]                  Extra classes on the row.
 * @param {(deviceId: string) => void} [onSpeakerChange]
 *        Called when the output device changes, in case the parent wants to
 *        route specific audio elements. As a best effort we also apply the
 *        chosen sink to every media element on the page.
 */
export default function DeviceControls({ className, onSpeakerChange }) {
  const { t } = useTranslation();
  const {
    getMics,
    getWebcams,
    localParticipant,
    selectedMicrophoneDevice,
    disableWebcam,
    enableWebcam,
  } = useMeeting();
  // The mic is enabled only on the VideoConsent screen, so a mic change here is
  // stashed in context (not applied via changeMic) and consumed there. The
  // webcam and speaker ids are likewise held in context — DeviceControls
  // remounts per journey page, so a local id would reset to the first device on
  // every navigation even though the meeting-level track (and the <video>) still
  // shows the picked one.
  const {
    selectedMicId,
    setSelectedMicId,
    selectedWebcamId,
    setSelectedWebcamId,
    selectedSpeakerId,
    setSelectedSpeakerId,
  } = useAppContext();
  const { getPlaybackDevices } = useMediaDevice();
  const { micStream } = useParticipant(localParticipant?.id);

  const [mics, setMics] = useState([]);
  const [webcams, setWebcams] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  // The selected webcam and speaker ids are owned in context, not locally: the
  // SDK's `selectedCameraDevice` from useMeeting() is unreliable (stale after
  // changeWebcam), there is no SDK-side speaker selection at all, and a local id
  // would reset on every page mount. Both default to the first enumerated device.

  const pubsubPublishMethod = usePubsubFunction();
  const { publish: deviceInfoPublish } = usePubSub("DEVICE_INFO");

  const loadDevices = useCallback(async () => {
    try {
      const [micList, camList, spkList] = await Promise.all([
        getMics(),
        getWebcams(),
        getPlaybackDevices(),
      ]);
      // Drop devices without a deviceId — before permissions fully settle
      // enumeration can yield an empty id, and Radix's <SelectItem> throws on an
      // empty-string value.
      const withId = (list) => (list || []).filter((d) => d.deviceId);
      const validSpeakers = withId(spkList);
      const validWebcams = withId(camList);
      setMics(withId(micList));
      setWebcams(validWebcams);
      setSpeakers(validSpeakers);
      // Default the speaker to the current selection once, if none chosen yet.
      setSelectedSpeakerId((prev) => prev || validSpeakers[0]?.deviceId || "");
      // Same for the camera — initial selection is the first enumerated webcam.
      setSelectedWebcamId((prev) => prev || validWebcams[0]?.deviceId || "");
    } catch (e) {
      // Permissions not ready yet — the lists just stay empty until retried.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial load + refresh on hot-plug (headset connected / camera unplugged…).
  useEffect(() => {
    loadDevices();
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return;
    md.addEventListener("devicechange", loadDevices);
    return () => md.removeEventListener("devicechange", loadDevices);
  }, [loadDevices]);

  // Keep the stashed mic id valid as devices come and go. If the picked mic is
  // unplugged, switch to the first available one; if none remain, null it — so
  // the picker trigger, the recorder's Device label, and the VideoConsent
  // capture all agree on the same fallback instead of disagreeing.
  useEffect(() => {
    if (!selectedMicId) return; // nothing picked yet — fallbacks handle display
    if (!mics.length) return; // not enumerated yet — empty ≠ "unplugged", don't clobber
    if (mics.some((m) => m.deviceId === selectedMicId)) return; // still present
    setSelectedMicId(mics[0]?.deviceId ?? null);
  }, [mics, selectedMicId, setSelectedMicId]);

  // Same for the camera: if the selected webcam is unplugged, fall back to the
  // first available one so the picker and the recorder's Device label agree.
  // The empty-list guard is critical: DeviceControls remounts on every page and
  // `webcams` is [] until loadDevices() resolves. Without it, this effect can't
  // find the persisted camera in the empty list, assumes it was unplugged, and
  // wipes selectedWebcamId — so the dropdown snaps back to camera [0] on each
  // navigation even though the picked camera is still streaming.
  useEffect(() => {
    if (!selectedWebcamId) return;
    if (!webcams.length) return; // not enumerated yet — empty ≠ "unplugged"
    if (webcams.some((w) => w.deviceId === selectedWebcamId)) return;
    setSelectedWebcamId(webcams[0]?.deviceId ?? "");
  }, [webcams, selectedWebcamId, setSelectedWebcamId]);

  // Same for the speaker, with the same empty-list guard — `speakers` is [] until
  // getPlaybackDevices() resolves on each remount, and clobbering the persisted
  // id there would reset the picker to speaker [0] on every navigation.
  useEffect(() => {
    if (!selectedSpeakerId) return;
    if (!speakers.length) return; // not enumerated yet — empty ≠ "unplugged"
    if (speakers.some((s) => s.deviceId === selectedSpeakerId)) return;
    setSelectedSpeakerId(speakers[0]?.deviceId ?? "");
  }, [speakers, selectedSpeakerId, setSelectedSpeakerId]);

  // Route audio output to the chosen speaker. VideoSDK has no changeSpeaker, so
  // we set the sink on every media element that supports it (best effort) and
  // hand the id to the parent for anything it manages itself. This re-runs on
  // every page mount, not just on change, so the media elements the new page
  // created also get the persisted sink instead of the browser default.
  useEffect(() => {
    if (!selectedSpeakerId) return;
    onSpeakerChange?.(selectedSpeakerId);
    document.querySelectorAll("audio, video").forEach((el) => {
      if (typeof el.setSinkId === "function") {
        el.setSinkId(selectedSpeakerId).catch(() => {});
      }
    });
  }, [selectedSpeakerId, onSpeakerChange]);

  // Publish the customer's selected device labels so the recorder's stats panel
  // (which cannot see the customer's local devices) can display them.
  useEffect(() => {
    // VideoSDK leaves `selectedMicrophoneDevice` unset until the customer
    // explicitly picks one, and the customer joins with the mic off — so fall
    // back to the first enumerated device, exactly as `speakerId` already
    // defaults to `spkList[0]`. Without this the recorder's Device card shows
    // a dash for mic and camera for the whole journey.
    const micLabel =
      mics.find(
        (m) => m.deviceId === (selectedMicId || selectedMicrophoneDevice?.deviceId),
      )?.label ||
      selectedMicrophoneDevice?.label ||
      mics[0]?.label ||
      null;
    const camLabel =
      webcams.find((w) => w.deviceId === selectedWebcamId)?.label ||
      webcams[0]?.label ||
      null;
    const spkLabel =
      speakers.find((s) => s.deviceId === selectedSpeakerId)?.label || null;

    if (micLabel || camLabel || spkLabel) {
      pubsubPublishMethod(
        deviceInfoPublish,
        "DeviceInfo",
        { persist: true },
        { microphone: micLabel, speaker: spkLabel, camera: camLabel },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mics, webcams, speakers, selectedMicId, selectedMicrophoneDevice, selectedWebcamId, selectedSpeakerId]);

  return (
    <div
      className={`grid w-full grid-cols-3 gap-2 sm:gap-3 ${className || ""}`}
    >
      {/* Microphone — live input meter pinned in the popover footer. */}
      <div className="min-w-0">
        <Select
          value={
            selectedMicId ||
            selectedMicrophoneDevice?.deviceId ||
            mics[0]?.deviceId ||
            ""
          }
          onValueChange={setSelectedMicId}
        >
          <SelectTrigger aria-label="Microphone">
            <MicrophoneIcon
              className="h-4 w-4 shrink-0 text-gray-150 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-xs text-black-150 sm:text-sm">
              <SelectValue placeholder={t("Select device")} />
            </span>
          </SelectTrigger>
          <SelectContent
            side="top"
            footer={<MicLevelMeter track={micStream?.track} />}
          >
            {mics.length ? (
              mics.map((m) => (
                <SelectItem key={m.deviceId} value={m.deviceId}>
                  {m.label}
                </SelectItem>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-150">
                {t("No devices found")}
              </div>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Speaker — "Test Speakers" pinned in the popover footer. */}
      <div className="min-w-0">
        <Select
          value={selectedSpeakerId || speakers[0]?.deviceId || ""}
          onValueChange={setSelectedSpeakerId}
        >
          <SelectTrigger aria-label="Speaker">
            <SpeakerWaveIcon
              className="h-4 w-4 shrink-0 text-gray-150 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-xs text-black-150 sm:text-sm">
              <SelectValue placeholder={t("Select device")} />
            </span>
          </SelectTrigger>
          <SelectContent
            side="top"
            footer={<TestSpeakersButton deviceId={selectedSpeakerId} />}
          >
            {speakers.length ? (
              speakers.map((s) => (
                <SelectItem key={s.deviceId} value={s.deviceId}>
                  {s.label}
                </SelectItem>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-150">
                {t("No devices found")}
              </div>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Camera. */}
      <div className="min-w-0">
        <Select
          value={selectedWebcamId || webcams[0]?.deviceId || ""}     
          onValueChange={async (id) => {
            disableWebcam();
            const track = await createCameraVideoTrack({ cameraId: id });
            console.log("DeviceControls: switching to webcam", id, track);
            enableWebcam(track);
            setSelectedWebcamId(id);
          }}
        >
          <SelectTrigger aria-label="Camera">
            <VideoCameraIcon
              className="h-4 w-4 shrink-0 text-gray-150 sm:h-5 sm:w-5"
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate text-xs text-black-150 sm:text-sm">
              <SelectValue placeholder={t("Select device")} />
            </span>
          </SelectTrigger>
          <SelectContent side="top">
            {webcams.length ? (
              webcams.map((w) => (
                <SelectItem key={w.deviceId} value={w.deviceId}>
                  {w.label}
                </SelectItem>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-gray-150">
                {t("No devices found")}
              </div>
            )}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
